import useCheckEcocashStatus from "api/PaymentsResource/useCheckEcocashStatus";
import { PaymentStatusCard } from "components";
import RetainBooksInPicks from "components/RetainBooksInPicks";
import useSearchParams from "hooks/useSearchParams";
import { Container } from "layouts";
import PaddingX from "layouts/PaddingX";
import { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import useAuthStore from "store/authStore";
import useSupportQueryStore from "store/supportQueryStore";

const SubscriptionResponse = () => {
	const [showListOfPicksToRetain, setShowListOfPicksToRetain] = useState(false);

	const { userProfile } = useAuthStore();
	const { setQuery } = useSupportQueryStore();
	const { getParam } = useSearchParams();
	const history = useHistory();
	const hasPicks = getParam("has_picks") === "true";
	const isSuccess = getParam("status") === "Successful";

	useEffect(() => {
		if (hasPicks && isSuccess) {
			setShowListOfPicksToRetain(true);
		} else {
			setShowListOfPicksToRetain(false);
		}

		return () => setShowListOfPicksToRetain(false);
	}, [hasPicks, isSuccess]);

	useEffect(() => {
		setQuery(
			`Hi, I am having issues with ecocash payment. Money was deducted from my wallet but the subscription was not processed successfully. Please help me resolve this issue: Reference: ${getParam(
				"reference"
			)}, Order ID: ${getParam("order_id")}, Phone: ${getParam("phone")}`
		);
	}, [getParam, setQuery]);

	const statusParamIsSuccess = getParam("status") === "Successful";
	const statusParamIsFailed = getParam("status") === "Failed";
	const shouldFetch = !statusParamIsSuccess && !statusParamIsFailed;

	const { isLoading, isTimedOut, elapsedTime, resetTimeout } = useCheckEcocashStatus(
		{
			order_id: getParam("order_id"),
		},
		shouldFetch
	);

	// Handle retry - reset timeout and continue polling
	const handleRetry = useCallback(() => {
		resetTimeout();
	}, [resetTimeout]);

	// Handle cancel - go back to subscriptions
	const handleCancel = useCallback(() => {
		history.push("/subscriptions");
	}, [history]);

	// Determine current status
	const getStatus = () => {
		if (statusParamIsSuccess) return "success";
		if (statusParamIsFailed) return "failure";
		if (isTimedOut) return "timeout";
		if (shouldFetch || isLoading) return "loading";
		return "failure";
	};

	const status = getStatus();

	// Build support URL with user info
	const supportUrl = `/support?name=${userProfile?.first_name} ${userProfile?.last_name}&email=${userProfile?.email}`;

	// Custom success message for subscription
	const successMessage = (
		<>
			Your <span className="font-semibold">EcoCash</span> subscription payment was recorded successfully.
			Browse books and pick the ones you want to enlist in your subscription.
		</>
	);

	// Get title based on status
	const getTitle = () => {
		switch (status) {
			case "success":
				return "Subscription Activated!";
			case "failure":
				return "Subscription Payment Failed";
			case "timeout":
				return "Confirmation Timeout";
			default:
				return "Confirming Payment";
		}
	};

	return (
		<Container>
			<PaddingX>
				<div className="mt-10 mb-10 flex flex-col justify-center items-center min-h-[60vh]">
					<PaymentStatusCard
						status={status}
						title={getTitle()}
						message={status === "success" ? successMessage : null}
						paymentMethod="EcoCash"
						primaryAction={
							status === "success"
								? { label: "Browse Books", to: "/books" }
								: status === "failure"
								? { label: "Try Again", to: "/subscriptions" }
								: null
						}
						secondaryAction={
							status === "success"
								? { label: "View My Picks", to: "/subscription/picks" }
								: null
						}
						supportUrl={status === "failure" || status === "timeout" ? supportUrl : null}
						reference={getParam("reference") || getParam("order_id")}
						paymentType="Subscription"
						elapsedTime={elapsedTime}
						onCancel={status === "loading" ? handleCancel : null}
						onRetry={status === "timeout" ? handleRetry : null}
					>
						{/* Subscription-specific: Tip about adding picks */}
						{status === "success" && (
							<div className="bg-green-50 rounded-lg p-4 text-sm text-gray-700">
								<p className="font-medium text-akgreener mb-1">How to use your subscription:</p>
								<p>
									When you find a book you like, click the "Add to picks" button to enlist it in your subscription.
								</p>
							</div>
						)}
					</PaymentStatusCard>

					{/* Retain Books Modal */}
					<RetainBooksInPicks
						showListOfPicksToRetain={showListOfPicksToRetain}
						setShowListOfPicksToRetain={setShowListOfPicksToRetain}
					/>
				</div>
			</PaddingX>
		</Container>
	);
};

export default SubscriptionResponse;
